import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle } from 'lucide-react';
import { buildApiUrl } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

interface MessageNotificationBadgeProps {
  className?: string;
}

export const MessageNotificationBadge: React.FC<MessageNotificationBadgeProps> = ({ className = '' }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const { user, logout } = useAuth();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (user) {
      loadUnreadCount();
      
      // Poll for updates every 30 seconds
      intervalRef.current = setInterval(loadUnreadCount, 30000);
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [user]);

  const loadUnreadCount = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token || !user) {
        setUnreadCount(0);
        return;
      }
      
      const response = await fetch(buildApiUrl('/api/messages/unread-count'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.status === 401) {
        // Token expired, stop polling and logout
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        setUnreadCount(0);
        logout();
        return;
      }
      
      const result = await response.json();
      if (result.success) {
        setUnreadCount(result.count || 0);
      }
    } catch (error) {
      // Silently handle errors to avoid console spam
      setUnreadCount(0);
    }
  };

  // Only show badge if user is authenticated
  if (!user) {
    return (
      <div className={`relative ${className}`}>
        <MessageCircle className="h-5 w-5" />
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <MessageCircle className="h-5 w-5" />
      {unreadCount > 0 && (
        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center leading-none">
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </div>
  );
};